import Swiper from "swiper";
import { EffectCards, Pagination, Keyboard } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-cards";
import "swiper/css/pagination";

import Swal from "sweetalert2";
import { TemplateEngine } from "../shared/utils/TemplateEngine.js";
import { PolizasService } from "../shared/services/PolizasService.js";
import { AccesosService } from "../shared/services/AccesosService.js";
import { EstadoService } from "../shared/services/EstadoService.js";
import { Navbar } from "../shared/components/navbar/Navbar.js";
import { esc } from "../shared/utils/Formato.js";
import homeTemplate from "./templates/home.html?raw";

const app = () => document.getElementById("app");

let swiper = null;
let polizasEnPantalla = [];

// La revalidacion vuelve despues de que la pantalla ya se pinto: si para entonces el usuario se
// fue al detalle de una poliza, pintar el home encima le arrebataria la pantalla.
const sigueEnHome = () => {
  const path = window.location.hash.slice(1).split("?")[0];
  return path === "" || path === "home";
};

const pantallaCargando = () => `
  ${Navbar.render()}
  <div class="fx-cargando">
    <div class="spinner-border text-primary" role="status"></div>
    <span class="small">Cargando tus pólizas...</span>
  </div>
`;

export const HomeController = {
  render: async (params) => {
    app().innerHTML = pantallaCargando();
    Navbar.bind();

    try {
      const forceRefresh = params?.get("refresh") === "1";

      // Con cache permanente esto resuelve al toque si ya hay datos guardados: el spinner de arriba
      // solo se ve la primera vez o cuando el usuario aprieta ↻.
      const polizas = await PolizasService.listar({ forceRefresh });
      HomeController.pintar(polizas);

      if (forceRefresh) {
        // La baja la pidio el usuario: se alinea el sello local para no repetirla enseguida.
        EstadoService.sincronizar();
        return;
      }

      // Revalidacion en segundo plano — sin await: la pantalla ya esta a la vista. Si el back no
      // tiene novedades (el caso normal) no se mueve nada.
      EstadoService.revalidar(async () => {
        const frescas = await PolizasService.listar({ forceRefresh: true });
        if (!sigueEnHome()) return;
        HomeController.pintar(frescas, { conservarTarjeta: true });
      });
    } catch (error) {
      console.error("Error cargando el home:", error);
      app().innerHTML =
        Navbar.render() +
        '<div class="fx-pantalla mt-4"><div class="alert alert-danger">' +
        '<i class="fas fa-triangle-exclamation me-2"></i>' +
        esc(error.message || "No pudimos cargar tus pólizas.") +
        '</div><button class="btn btn-outline-primary w-100" id="btnReintentar">' +
        '<i class="fas fa-rotate me-2"></i>Reintentar</button></div>';
      Navbar.bind();
      document.getElementById("btnReintentar")?.addEventListener("click", () => HomeController.render());
    }
  },

  /**
   * Pinta el home. Separado de render() porque la revalidacion en segundo plano repinta sin
   * volver a pasar por el spinner ni por la carga.
   *
   * @param {object} [opciones]
   * @param {boolean} [opciones.conservarTarjeta] Vuelve a la tarjeta que el usuario estaba mirando.
   */
  pintar: (polizas, { conservarTarjeta = false } = {}) => {
    // Se lee ANTES del innerHTML: despues la instancia de Swiper ya no sirve.
    const indice = conservarTarjeta && swiper ? swiper.activeIndex : 0;

    polizasEnPantalla = polizas;
    const accesos = AccesosService.listar();

    const contenido = TemplateEngine.render(homeTemplate, {
      Polizas: polizas,
      HayPolizas: polizas.length > 0,
      Accesos: accesos,
      HayAccesos: accesos.length > 0,
      ContactoHref: AccesosService.whatsappContacto("Quiero consultar por mis pólizas."),
    });

    app().innerHTML = Navbar.render() + contenido;
    Navbar.bind();
    // Clamp: si el refresco trajo menos polizas, el indice viejo podria no existir.
    HomeController.bind(Math.min(indice, Math.max(polizas.length - 1, 0)));
  },

  bind: (indiceInicial = 0) => {
    document.getElementById("btnRefrescar")?.addEventListener("click", async () => {
      await HomeController.render(new URLSearchParams("refresh=1"));
    });

    // Los accesos "próximamente" son links con href="#": se cancela la navegación y se avisa.
    document.querySelectorAll('.fx-acceso[data-accion="proximamente"]').forEach((acceso) => {
      acceso.addEventListener("click", (e) => {
        e.preventDefault();
        Swal.fire({
          icon: "info",
          iconColor: "#12608f",
          title: acceso.dataset.titulo,
          text: "Estamos trabajando en esta sección. ¡Muy pronto vas a poder usarla!",
          confirmButtonText: "Entendido",
          confirmButtonColor: "#12608f",
        });
      });
    });

    // Cada tarjeta abre su detalle (click o Enter, porque son role="button")
    document.querySelectorAll(".fx-cred").forEach((tarjeta) => {
      const abrir = () => {
        window.location.hash = "poliza?id=" + tarjeta.dataset.id;
      };
      tarjeta.addEventListener("click", abrir);
      tarjeta.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          abrir();
        }
      });
    });

    if (!document.getElementById("swiperPolizas")) return;

    // Una instancia por render: sin el destroy, volver al home deja listeners colgados.
    if (swiper) {
      swiper.destroy(true, true);
      swiper = null;
    }

    swiper = new Swiper("#swiperPolizas", {
      modules: [EffectCards, Pagination, Keyboard],
      effect: "cards",
      grabCursor: true,
      cardsEffect: {
        perSlideOffset: 9,
        perSlideRotate: 3,
        slideShadows: true,
      },
      pagination: { el: ".swiper-pagination", clickable: true },
      keyboard: { enabled: true },
      on: {
        slideChange: () => HomeController.pintarResumen(swiper.activeIndex),
        touchStart: () => document.getElementById("pistaSlider")?.classList.add("fx-pista--oculta"),
      },
    });

    // Duracion 0: tras un repintado por datos nuevos, la tarjeta tiene que aparecer donde estaba,
    // no viajar hasta ahi.
    if (indiceInicial > 0) swiper.slideTo(indiceInicial, 0);
    HomeController.pintarResumen(indiceInicial);
  },

  /** Bloque de datos bajo el slider: acompaña a la tarjeta que está adelante. */
  pintarResumen: (indice) => {
    const contenedor = document.getElementById("resumenActivo");
    const poliza = polizasEnPantalla[indice];
    if (!contenedor || !poliza) return;

    // HTML armado fuera del TemplateEngine → los valores se escapan a mano con esc().
    contenedor.innerHTML = `
      <div class="fx-seccion__titulo">
        <i class="fas fa-circle-info"></i>${esc(poliza.Compania)}
      </div>
      <div class="fx-campo">
        <div class="fx-campo__etiqueta">Mi plan</div>
        <div class="fx-campo__valor">${esc(poliza.Plan)}</div>
      </div>
      <div class="row g-0">
        <div class="col-6">
          <div class="fx-campo">
            <div class="fx-campo__etiqueta">Número de póliza</div>
            <div class="fx-campo__valor">${esc(poliza.NumeroPoliza)}</div>
          </div>
        </div>
        <div class="col-6">
          <div class="fx-campo">
            <div class="fx-campo__etiqueta">Vencimiento</div>
            <div class="fx-campo__valor">${esc(poliza.VencimientoTexto)}</div>
          </div>
        </div>
      </div>
      <div class="p-3">
        <a class="btn btn-primary w-100" href="#poliza?id=${encodeURIComponent(poliza.Id)}">
          <i class="fas fa-file-lines me-2"></i>Ver detalle de la póliza
        </a>
      </div>
    `;
  },
};
